import React, { useState } from "react";
import { Container, Row, Table } from "reactstrap";
import { CenteredSpinner } from "../Common/CenteredSpinner";
import KaAlert from "../Common/KaAlert";
import useDisplayMessage from "../Common/useDisplayMessage";
import FilterInput, { textSatisfiesFilter } from "../FilterInput";
import { CreateEditCarrier } from "./CreateEditCarrier";
import { modalMode } from "../Common/EntityModal";
import PermissionButton from "../UserAccess/PermissionButton";
import { usePermission } from "../UserAccess/usePermission";
import EditViewButton from "../UserAccess/EditViewButton";
import { Button } from "react-bootstrap";
import Confirm from "../Common/Confirm";
import useGetCarriers, { CarrierDto } from "../../requests/useGetCarriers";
import useRemoveCarrier from "../../requests/useRemoveCarrier";
import { SiteFilterPill, useSiteFilter } from "../UserAccess/SiteScope/SiteFilterPill";
import { SiteColumnCell, SiteColumnHeader } from "../Common/SiteColumn";
import { ActionsColumnHeader } from "../Common/ActionsColumn";

const alphabeticallyByName = (a: CarrierDto, b: CarrierDto) => a.name.localeCompare(b.name);

const CarrierPage = () => {
  const displayMessage = useDisplayMessage();
  const [filterText, setFilterText] = useState('');
  const [createEditCarrier, setCreateEditCarrier] = useState<{ carrier?: CarrierDto } | undefined>(undefined);
  const [carrierToRemove, setCarrierToRemove] = useState<CarrierDto | undefined>(undefined);
  const canModify = usePermission('otherEntitiesModify');
  const siteFilter = useSiteFilter();

  const getCarrier = useGetCarriers();

  const containsFilterText = (carrier: CarrierDto) => {
    const satisfiesFilter = textSatisfiesFilter(filterText);
    return satisfiesFilter(carrier.name) || satisfiesFilter(carrier.number);
  };

  const filterTextChanged = (text: string) => setFilterText(text);

  const removeSuccess = () => {
    displayMessage.success(`Carrier ${carrierToRemove?.name} removed`);
    return getCarrier.query();
  };

  const removeRequest = useRemoveCarrier(carrierToRemove?.id ?? '', {
    onSuccess: removeSuccess,
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setCarrierToRemove(undefined),
  });

  const createClicked = () => {
    displayMessage.clear();
    setCreateEditCarrier({});
  }

  const editClicked = (carrier: CarrierDto) => {
    displayMessage.clear();
    setCreateEditCarrier({ carrier });
  }

  const carrierUpserted = (carrierId: string, carrierName: string) => {
    displayMessage.success(`Carrier ${carrierName} ${createEditCarrier?.carrier ? 'updated' : 'created'}`);
    getCarrier.query();
  }

  const editDismissed = () => setCreateEditCarrier(undefined);

  const visibleCarriers = (getCarrier.data ?? [])
    .filter(containsFilterText)
    .filter(carrier => siteFilter.matches(carrier.sites))
    .sort(alphabeticallyByName);

  const loadingRows = getCarrier.isLoading || removeRequest.isLoading;

  if (getCarrier.isError) return <h3>{getCarrier.error?.message}</h3>

  return <Container>
    {!!createEditCarrier &&
      <CreateEditCarrier
        initialCarrier={createEditCarrier.carrier ?? null}
        mode={modalMode(canModify, !createEditCarrier.carrier)}
        onUpsert={carrierUpserted}
        onDismiss={editDismissed} />}
    <Confirm
      visible={!!carrierToRemove}
      title='Remove carrier'
      body={`Are you sure that you want to remove carrier ${carrierToRemove?.name}?`}
      isLoading={removeRequest.isLoading}
      onDismiss={() => setCarrierToRemove(undefined)}
      onConfirm={() => removeRequest.request()} />
    <Row className='justify-content-between mb-2'>
      <h2 className='col-auto ka-blue'>Carriers</h2>
      <PermissionButton permission="otherEntitiesModify" className='col-auto btn-ghost-primary' onClick={createClicked}>Create Carrier</PermissionButton>
    </Row>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <form>
      <div className='row gx-2 gx-md-3 mb-3'>
        <div className='col-md-4 mb-2 mb-md-0'>
          <FilterInput placeholder='Search by name or number' onFilterTextChange={filterTextChanged} value={filterText} />
        </div>
        <div className='col-auto d-flex align-items-center'>
          <SiteFilterPill filter={siteFilter} />
        </div>
      </div>
    </form>
    <Table bordered className="preserve-whitespace" style={{tableLayout: "fixed"}}>
      <thead>
        <tr>
          <th className="col-3">Name</th>
          <th className="col-2">Number</th>
          <th>Notes</th>
          <SiteColumnHeader />
          <ActionsColumnHeader />
        </tr>
      </thead>
      <tbody>
        {!loadingRows && visibleCarriers.map(carrier =>
          <tr key={carrier.id}>
            <td>{carrier.name}</td>
            <td>{carrier.number}</td>
            <td>
              <div className="crop-text-3">{carrier.notes}</div>
            </td>
            <SiteColumnCell sites={carrier.sites} />
            <td align="right" className="text-nowrap">
              <div className='dropdown'>
                <Button id='actionsMenuButton'
                  className='dropdown-toggle btn-ghost-secondary'
                  variant='link'
                  data-bs-toggle='dropdown'
                  aria-expanded='false'><strong>Actions</strong></Button>
                <div className='dropdown-menu' aria-labelledby='actionsMenuButton'>
                  <EditViewButton
                    canModify={canModify}
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => editClicked(carrier)} />
                  <PermissionButton permission="otherEntitiesModify"
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => setCarrierToRemove(carrier)}>Remove</PermissionButton>
                </div>
              </div>
            </td>
          </tr>)}
        {(loadingRows || visibleCarriers.length === 0) &&
          <tr>
            <td colSpan={5} className='text-center text-muted py-3'>
              {loadingRows
                ? <CenteredSpinner />
                : siteFilter.emptyMessage('No carriers to show')}
            </td>
          </tr>}
      </tbody>
    </Table>
  </Container>
};

export { CarrierPage };
